






























import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { ValidationObserverInstance, ValidationProvider } from "vee-validate";
import { AttributesHelper } from "../../lib/utils/AttributesHelper";

/**
 * This component is an advanced input textarea
 */
@Component({
  components: {
    ValidationProvider
  }
})
export default class AnxTextarea extends Vue {
  /** This is the name of the textarea. A unqiue id will be created if this attribute is unset */
  @Prop({ default: null }) id!: string | null;
  /** This is the name of the textarea */
  @Prop({ default: "anx-textarea" }) name!: string;
  /** This will be shown as field name in error messages. Can be used for localization */
  @Prop({ default: null }) dataVvAs!: string | null;
  /** This is the label of the textarea that will be displayed */
  @Prop({ default: "Additional Text" }) label!: string;
  /** This is the number of rows the textarea should have */
  @Prop({ default: "4" }) rows!: string;
  /** With this property you can set the textarea to disabled */
  @Prop({ default: null }) disabled!: boolean | null;
  /** This is the width of the textarea */
  @Prop({ default: "100%" }) width!: string;
  /**
   * If this property is set to true, a standard *required* validation will be applied on the textarea
   * @deprecated Use the *rules* property instead to define rules
   */
  @Prop({ default: null }) validation!: boolean | null;
  /** These are the rules for validation. See [VeeValidate Rules](https://vee-validate.logaretm.com/v2/guide/rules.html). */
  @Prop({ default: null }) rules!: string;
  /** This is the value passed automatically via v-model */
  @Prop({ default: null }) value!: string;
  /** HTML autocomplete attribute */
  @Prop({ default: "" }) autocomplete!: string;

  private active = false;
  private filled = false;

  /** The id that is actually used */
  private inputId: string | undefined = "";

  /** This variable is used as v-model for the input field (Using the value variable directly is not allowed because props should not be mutated directly) */
  private localValue = "";

  get cssProps() {
    return {
      "--textarea-width": this.width
    };
  }

  /**
   * Watch the localValue variable used as v-model for the textarea
   * When it changes, the users is typing and changed some text, it has
   * to be checked if the textarea should be active or not.
   */
  @Watch("localValue")
  localValueChanged(newVal: string) {
    if (newVal && newVal.length) {
      this.active = true;
    }
  }

  /**
   * Watch the attribute/prop value.
   * When this variable changes it means that the value for the textarea has been
   * updated from the parent component programmatically. The localValue variable has to
   * be assigned and it has to be checked, if the textarea is filled
   */
  @Watch("value")
  valueChanged() {
    this.localValue = this.value !== null ? this.value : "";
    this.isFilled();
  }

  /**
   * Set the id for the component when created
   */
  private created() {
    this.inputId = AttributesHelper.attributes(this, {
      uniqueId: true,
      uniqueIdPrefix: "anx-textarea-"
    }).id;
  }

  /**
   * After mount, the prop value has to be assigned to the localValue and it has to be checked,
   * if the input field should be filled
   */
  private mounted() {
    this.localValue = this.value !== null ? this.value : "";
    this.isFilled();
  }

  /** Do a validation if the component is updated */
  private updated() {
    /** Only validate, if the value is set */
    if (this.value && this.value !== "") this.validate();
  }

  /**
   * This function is needed, because the validation property is obsolete, but is used in some applications.
   * Backward compatibility is implemented by this function.
   * In the old anx-textarea, if the validation property was set, the rules would be required by default, without setting it.
   */
  private get validationRules() {
    if (this.validation !== null && this.rules === null) {
      return "required";
    }
    return this.rules;
  }

  /** Check if the input-field is filled, Set class filled. */
  protected isFilled() {
    if (this.localValue && this.localValue.length > 0) {
      this.filled = true;
    } else {
      this.filled = false;
    }
  }

  /**When the User click in and out of the field, the state active will be set and
   * the state filled will be checked.
   */
  protected async inputBlur() {
    /** Trigger a validation manually, when focus is lost */
    this.validate();

    this.active = !this.active;
    this.isFilled();
  }

  /** Validate the input with the defined rules */
  public async validate() {
    if (this.validationRules !== null) {
      await (this.$refs.observer as ValidationObserverInstance).validate();
    }
  }

  /**
   * This function is called, when the user clicks the input field
   * If the field is readonly, nothing should happen
   */
  private clickInputField() {
    this.active = true;
  }
}
